import type MemberSearchHit from "@travily/types/pending/models/MemberSearchHit";
import { type LngLatBoundsLike, MapPopup, Marker } from "@ui/components/Map";
import { useMemo, useState } from "react";
import { AccountAvatar } from "@/components/AccountAvatar/AccountAvatar";
import { useMap } from "@/layouts/map-host";
import type { MapBounds } from "@/types/search-map";
import { MemberMapPinCard } from "./components/MemberMapPinCard";

// Rounded, matching the /destinations and /programs result maps.
const MAP_CLASSNAME = "size-full overflow-hidden rounded-3xl";

type LocatedHit = MemberSearchHit & { location: { lat: number; lon: number } };

// Mirrors map-host's `computeBoundsFromPoints` (not exported). Pads a single
// point so a lone member doesn't zoom to street level.
function computeBounds(points: { lat: number; lon: number }[]): LngLatBoundsLike | undefined {
  const first = points[0];
  if (!first) return undefined;
  let minLat = first.lat;
  let maxLat = first.lat;
  let minLon = first.lon;
  let maxLon = first.lon;
  for (const p of points) {
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lon < minLon) minLon = p.lon;
    if (p.lon > maxLon) maxLon = p.lon;
  }
  if (minLat === maxLat && minLon === maxLon) {
    const pad = 0.5;
    return [
      [minLon - pad, minLat - pad],
      [maxLon + pad, maxLat + pad],
    ];
  }
  return [
    [minLon, minLat],
    [maxLon, maxLat],
  ];
}

interface MembersMapProps {
  hits: MemberSearchHit[];
  /** True once the user has panned/zoomed — suppresses auto-fitting to hits. */
  userPanned: boolean;
  onUserBoundsChange: (bounds: MapBounds) => void;
}

/**
 * Full-width members map (rounded, inset from the viewport edges). Renders each
 * located member as a circular profile-photo marker, reusing the persistent
 * Mapbox instance via `useMap`. Panning re-runs the search via `onUserBoundsChange`.
 *
 * Built on the lower-level `useMap` rather than the shared `SearchResultsMap`
 * because that helper is a sticky side-panel with default pins; members want a
 * map on top with avatar markers.
 */
export function MembersMap({ hits, userPanned, onUserBoundsChange }: MembersMapProps) {
  const [selectedHandle, setSelectedHandle] = useState<string | null>(null);

  const located = useMemo(() => hits.filter((hit): hit is LocatedHit => hit.location != null), [hits]);

  const hitsBounds = useMemo(() => computeBounds(located.map((hit) => hit.location)), [located]);
  // Auto-fit to all located members until the user takes over the view by panning.
  const fitBounds = userPanned ? undefined : hitsBounds;

  const selected = useMemo(
    () => (selectedHandle != null ? (located.find((hit) => hit.handle === selectedHandle) ?? null) : null),
    [located, selectedHandle],
  );

  // Markers are memoized independently of the selection so opening/closing a popup
  // doesn't re-create and re-reconcile every avatar marker.
  const markers = useMemo(
    () =>
      located.map((hit) => (
        <Marker
          anchor="bottom"
          key={hit.handle}
          latitude={hit.location.lat}
          longitude={hit.location.lon}
          onClick={(e) => {
            e.originalEvent.stopPropagation();
            setSelectedHandle(hit.handle);
          }}
        >
          <div className="cursor-pointer rounded-full border-2 border-white shadow-md">
            <AccountAvatar
              account={{ profile_type: hit.type, avatar: hit.avatar, initial: hit.initial }}
              shape="circle"
              size="44"
            />
          </div>
        </Marker>
      )),
    [located],
  );

  const popup = useMemo(
    () =>
      selected ? (
        <MapPopup
          latitude={selected.location.lat}
          longitude={selected.location.lon}
          onClose={() => setSelectedHandle(null)}
        >
          <MemberMapPinCard hit={selected} onClose={() => setSelectedHandle(null)} />
        </MapPopup>
      ) : null,
    [selected],
  );

  const overlay = useMemo(
    () => (
      <>
        {markers}
        {popup}
      </>
    ),
    [markers, popup],
  );

  const slotRef = useMap({
    overlay,
    className: MAP_CLASSNAME,
    fitBounds: fitBounds ?? undefined,
    onUserBoundsChange,
    showFullscreen: true,
  });

  // Desktop-only, mirroring the other explore maps. Horizontal + top padding insets
  // the rounded map from the viewport edges; the inner div is the teleport slot.
  return (
    <div className="hidden px-5 pt-5 md:px-8 md:pt-8 lg:block">
      <div className="h-[420px] w-full lg:h-[520px]" ref={slotRef} />
    </div>
  );
}
